import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogPosts } from '../data/blog.js';

const Article = () => {
  const { id } = useParams();
  const post = blogPosts.find(p => p.id === id);

  // Update document title and meta for SEO
  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Cătălin Dorca — Personal Trainer`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', post.excerpt);

      // Add article structured data
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'article-schema';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.title,
        description: post.excerpt,
        datePublished: post.date,
        dateModified: post.date,
        wordCount: post.content.split(/\s+/).length,
        inLanguage: 'ro',
        author: {
          '@type': 'Person',
          name: 'Cătălin Dorca',
          url: 'https://fitxbydorca.ro',
          jobTitle: 'Personal Trainer'
        },
        publisher: {
          '@type': 'Person',
          name: 'Cătălin Dorca',
          url: 'https://fitxbydorca.ro'
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://fitxbydorca.ro/article/${post.id}`
        },
        about: {
          '@type': 'Thing',
          name: 'Fitness și nutriție'
        }
      });
      document.head.appendChild(script);

      return () => {
        document.title = 'Cătălin Dorca — Personal Trainer Online Cluj-Napoca';
        const existing = document.getElementById('article-schema');
        if (existing) existing.remove();
      };
    }
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-white dark:bg-dark-950 flex items-center justify-center px-5">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-dark-900 dark:text-white mb-4">Articolul nu a fost găsit</h1>
          <Link to="/" className="text-primary hover:underline">Înapoi pe pagina principală</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-dark-950">
      <article className="max-w-2xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Înapoi
        </Link>

        <header className="mb-10">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-dark-900 dark:text-white tracking-tight leading-tight mb-4">
            {post.title}
          </h1>
          <div className="flex items-center gap-3 text-sm text-dark-400">
            <span>Cătălin Dorca</span>
            <span className="text-dark-300 dark:text-dark-600">|</span>
            <time dateTime={post.date}>{new Date(post.date).toLocaleDateString('ro-RO', { year: 'numeric', month: 'long', day: 'numeric' })}</time>
            <span className="text-dark-300 dark:text-dark-600">|</span>
            <span>{post.readTime} min citire</span>
          </div>
        </header>

        <div className="space-y-4">
          {post.content.split('\n').map((para, i) => {
            if (!para.trim()) return null;
            return (
              <p key={i} className="text-base text-dark-700 dark:text-dark-300 leading-relaxed">
                {para}
              </p>
            );
          })}
        </div>

        {/* CTA at bottom of article */}
        <div className="mt-14 p-6 sm:p-8 rounded-2xl bg-dark-50 dark:bg-dark-900 border border-dark-200/60 dark:border-dark-700/50 text-center">
          <p className="text-base font-semibold text-dark-900 dark:text-white mb-2">
            Vrei ajutor personalizat?
          </p>
          <p className="text-sm text-dark-500 dark:text-dark-400 mb-5">
            Aplică pentru o evaluare gratuită și primești un plan adaptat obiectivelor tale.
          </p>
          <a href="/quiz" className="btn-primary text-sm">
            Aplică acum
          </a>
        </div>

        {/* Related posts */}
        <div className="mt-14">
          <h2 className="text-lg font-bold text-dark-900 dark:text-white mb-6">Citește și</h2>
          <div className="space-y-4">
            {blogPosts
              .filter(p => p.id !== post.id)
              .slice(0, 3)
              .map(related => (
                <a
                  key={related.id}
                  href={`/article/${related.id}`}
                  className="block p-4 rounded-xl bg-dark-50 dark:bg-dark-900 border border-dark-200/60 dark:border-dark-700/50 hover:border-primary/30 transition-colors"
                >
                  <h3 className="text-sm font-semibold text-dark-900 dark:text-white mb-1">{related.title}</h3>
                  <p className="text-xs text-dark-500 dark:text-dark-400">{related.readTime} min citire</p>
                </a>
              ))}
          </div>
        </div>
      </article>
    </div>
  );
};

export default Article;
